/** All user-facing copy of the till scanner (Czech). */
import type { Tier } from "./api-types";

const czk = new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 0 });

/** Czech plural: 1 bod, 2–4 body, 0/5+ bodů. */
function points(n: number): string {
  const abs = Math.abs(n);
  const word = abs === 1 ? "bod" : abs >= 2 && abs <= 4 ? "body" : "bodů";
  return `${czk.format(n)} ${word}`;
}

export const strings = {
  appTitle: "GustoPass pokladna",
  loading: "Načítám…",
  retry: "Zkusit znovu",
  back: "Zpět",

  signInTitle: "Přihlášení obsluhy",
  signInAccessNote: "V provozu přihlašuje Cloudflare Access automaticky.",
  signInDevLabel: "Vývojový token (X-Staff-Dev-Token)",
  signInDevHint: "Jen pro vývoj a testy. Token se uloží v tomto zařízení.",
  signInContinue: "Pokračovat",
  signInViaAccess: "Pokračovat přes Access",
  signOut: "Odhlásit",
  sessionExpired: "Přihlášení vypršelo nebo token neplatí. Přihlaste se znovu.",

  tillTitle: "Pokladna",
  tillMall: "Centrum",
  tillBrand: "Restaurace",
  tillChoose: "Vyberte…",
  tillSave: "Uložit pokladnu",
  tillChange: "Změnit pokladnu",
  tillLoadFailed: "Nepodařilo se načíst restaurace a centra.",

  scanTitle: "Načtěte kartu hosta",
  scanCamera: "Skenovat QR",
  scanStop: "Zastavit kameru",
  scanCameraUnavailable: "Kamera není k dispozici. Zadejte číslo karty a PIN.",
  scanCameraStarting: "Spouštím kameru…",
  scanManual: "Ruční zadání",
  scanCardNumber: "Číslo karty (11 číslic)",
  scanPin: "PIN (6 číslic)",
  scanQrText: "Nebo vložte obsah QR kódu",
  scanSubmit: "Načíst hosta",
  scanInvalidCard: "Číslo karty má 11 číslic a PIN 6 číslic.",
  scanUnknownCode: "Tohle není QR kód GustoPass.",

  memberPoints: "Body",
  memberTier: "Úroveň",
  memberIsic: "ISIC",
  memberIsicYes: "ověřeno",
  memberIsicNo: "neověřeno",
  memberCoupons: "Dostupné kupony",
  memberNoCoupons: "Žádné dostupné kupony",
  memberReservation: "Rezervovaný kupon",
  memberPack: "Obědový balíček",
  memberPackProgress: (used: number, meals: number, validUntil: string) => `${used}/${meals} obědů, platí do ${validUntil}`,

  billTitle: "Účet",
  billTotal: "Celkem (Kč)",
  billReceipt: "Číslo účtenky",
  billLines: "Položky (nepovinné)",
  billLineName: "Název",
  billLinePrice: "Cena",
  billAddLine: "Přidat položku",
  billRemoveLine: "Odebrat",
  billCoupon: "Uplatnit kupon",
  billNoCoupon: "Bez kuponu",
  billScanReservation: "Naskenovat rezervaci",
  billReservationOther: "Rezervace patří jinému hostovi.",
  billPackSale: "Prodej obědového balíčku",
  billPackNone: "Neprodávat",
  billPackOption: (meals: number, price: number) => `${meals} obědů za ${czk.format(price)} Kč`,
  billPackUnavailable: "Katalog balíčků není dostupný.",
  billPackMealUsed: "Oběd z balíčku",
  billIsicVerified: "ISIC ověřen obsluhou",
  billIsicSaving: "Ukládám ISIC…",
  billIsicFailed: "ISIC se nepodařilo uložit.",
  billSubmit: "Zaúčtovat",
  billSubmitting: "Zaúčtovávám…",
  billCancel: "Zrušit",
  billInvalidTotal: "Zadejte celkovou částku v korunách.",
  billInvalidReceipt: "Zadejte číslo účtenky.",
  billInvalidLine: "Každá položka potřebuje název a cenu.",

  resultTitle: "Zaúčtováno",
  resultQueuedTitle: "Čeká na odeslání",
  resultQueuedNote: "Pokladna je offline. Účet se odešle automaticky, jakmile bude spojení.",
  resultReplayed: "Tento účet už byl zaúčtován dříve (opakované odeslání).",
  resultPointsEarned: (n: number) => `+${points(n)}`,
  resultNewBalance: "Nový zůstatek",
  resultSaved: "Ušetřeno",
  resultNewStamp: "Nové razítko",
  resultRewards: "Získané odměny",
  resultTierChanged: (tier: string) => `Nová úroveň: ${tier}`,
  resultNext: "Další host",

  queueBadge: (n: number) => `${n} čeká na odeslání`,
  queueTitle: "Fronta k odeslání",
  queueFailed: "Odmítnuto serverem",
  queueRemove: "Odebrat",
  queueSendNow: "Odeslat nyní",

  offline: "Offline",
  errorNetwork: "Server je nedostupný.",
  errorGeneric: "Něco se nepovedlo.",
  errorByCode: {
    invalid_token: "QR kód je neplatný.",
    token_expired: "QR kód vypršel. Ať host obnoví kartu.",
    nonce_reused: "QR kód už byl použit.",
    invalid_pin: "Nesprávný PIN.",
    too_many_attempts: "Příliš mnoho pokusů. Zkuste to za minutu.",
    member_not_found: "Host nenalezen.",
  } as Record<string, string>,

  tierName: { host: "Host", stamgast: "Štamgast", labuznik: "Labužník" } satisfies Record<Tier, string>,
  czk: (n: number) => `${czk.format(n)} Kč`,
  points,
};
